import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { translateGameName } from '../utils/gameNameI18n';
import './AchievementsPanel.css';

const CATEGORY_ORDER = ['level', 'battle', 'arena', 'craft', 'social'];

/** Başarımlar: ilerleme, ödül alma ve profilde gösterilecek unvan seçimi. */
const AchievementsPanel = ({ onCharacterChange }) => {
  const { t } = useTranslation('achievements');
  const [list, setList] = useState(null);
  const [titles, setTitles] = useState([]);
  const [activeTitle, setActiveTitle] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  const apply = (d) => {
    if (!d?.success) return;
    setList(d.achievements || []);
    setTitles(d.titles || []);
    setActiveTitle(d.activeTitle || '');
  };

  const load = useCallback(async () => {
    try {
      const res = await axios.get('/api/achievements');
      apply(res.data);
    } catch (_) {
      setList([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const claim = async (id) => {
    if (busy) return;
    setBusy(id);
    setMsg('');
    try {
      const res = await axios.post('/api/achievements/claim', { id });
      if (res.data?.success) {
        apply(res.data);
        setMsg(t('claimedMsg', { ruh: res.data.rewardRuh || 0 }));
        onCharacterChange?.();
      } else if (res.data?.message) {
        setMsg(res.data.message);
      }
    } finally {
      setBusy('');
    }
  };

  const chooseTitle = async (title) => {
    try {
      const res = await axios.post('/api/achievements/title', { title });
      if (res.data?.success) setActiveTitle(res.data.activeTitle || '');
      else if (res.data?.message) setMsg(res.data.message);
    } catch (_) { /* yoksay */ }
  };

  const groups = useMemo(() => {
    const g = {};
    (list || []).forEach((a) => {
      (g[a.category] = g[a.category] || []).push(a);
    });
    return CATEGORY_ORDER.filter((c) => g[c]).map((c) => [c, g[c]]);
  }, [list]);

  if (list === null) return <div className="item-market-loading">…</div>;
  const completed = list.filter((a) => a.completed).length;

  return (
    <div className="ach-panel">
      <div className="ach-summary">
        <span>{t('progressSummary', { done: completed, total: list.length })}</span>
        <label className="ach-title-pick">
          {t('activeTitle')}
          <select value={activeTitle} onChange={(e) => chooseTitle(e.target.value)}>
            <option value="">{t('noTitle')}</option>
            {titles.map((ti) => (
              <option key={ti} value={ti}>{translateGameName(ti)}</option>
            ))}
          </select>
        </label>
      </div>
      {msg ? <p className="ach-msg">{msg}</p> : null}
      {groups.map(([cat, items]) => (
        <section key={cat} className="ach-group">
          <h3 className="level-quests-section-title">{t(`cat_${cat}`)}</h3>
          <ul className="ach-list">
            {items.map((a) => {
              const pct = Math.min(100, Math.round((a.progress / Math.max(1, a.target)) * 100));
              return (
                <li key={a.id} className={`ach-item${a.completed ? ' ach-item--done' : ''}${a.claimed ? ' ach-item--claimed' : ''}`}>
                  <div className="ach-item__head">
                    <span className="ach-item__name">{translateGameName(a.name)}</span>
                    {a.title ? <span className="ach-item__title">🏷 {translateGameName(a.title)}</span> : null}
                  </div>
                  <div className="ach-item__desc">{translateGameName(a.desc)}</div>
                  <div className="ach-bar"><div className="ach-bar__fill" style={{ width: `${pct}%` }} /><span>{a.progress}/{a.target}</span></div>
                  <div className="ach-item__foot">
                    <span className="ach-item__reward">{a.rewardRuh ? t('rewardRuh', { ruh: a.rewardRuh }) : ''}</span>
                    {a.completed && !a.claimed ? (
                      <button type="button" className="auth-button auth-button--compact ach-claim" disabled={busy === a.id} onClick={() => claim(a.id)}>
                        {busy === a.id ? '…' : t('claim')}
                      </button>
                    ) : a.claimed ? <span className="ach-done">✓ {t('claimed')}</span> : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
};

export default AchievementsPanel;
